/**
 * CypAdhdSsiDomainPanel — one area of functional impairment, laid out as on
 * the adult SSI's Domains page.
 *
 * Same chrome as CypAdhdSsiSymptomPanel — an "Area N of M" counter, then one
 * card whose header carries the decision — so the two walks read as one
 * section rather than two designs. Navigation is deliberately NOT in either
 * panel: the page owns it through ModularStepFooter, so the primary action
 * never needs scrolling to reach.
 *
 * There are no settings or examples here: impairment is recorded per area with
 * a determination and the clinician's account of what it looks like. That
 * matches the adult Domains page, which likewise drops the settings pills.
 */

import React from 'react';
import StructuredNotesEditor from '../../12_observation/components/StructuredNotesEditor';
import InfoTooltip from '../../../../components/InfoTooltip';
import { CYP_NOTE_TAGGING_ENABLED, CYP_NOTE_TAG_OPTIONS } from '../../../../constants/cypAdhdNoteTagOptions';

// Suggested prompts stay authored on each area so they can be shown again
// without being rewritten. They are hidden for now.
const SHOW_SUGGESTED_PROMPTS = false;

/**
 * Yes / No for "at least moderate impairment" in this domain. Adult ADHD
 * asks the same threshold ("Moderate or severe impairment") once per life
 * period; a child is in one period, so there is one pair of buttons.
 *
 * The stored field stays `isImpairedSelection`. Renaming it would orphan
 * data.functionalImpairment.{domain}.isImpairedSelection on every existing
 * stage document, and the extractor, the review page and the prompt inputs
 * all read that path.
 */
// Clicking the button that is already on clears the answer, so an
// accidental Yes or No is not stuck.
function DecisionButtons({ value, onSelect, disabled, groupLabel }) {
  const base = 'grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex transition-all duration-300';
  return (
    <div className="grow shrink basis-0 h-9 justify-start items-center gap-3 flex" role="group" aria-label={groupLabel}>
      <button
        type="button"
        onClick={() => onSelect(value === true ? null : true)}
        disabled={disabled}
        aria-pressed={value === true}
        className={`${base} ${value === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white'} ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
      >
        <div className="px-0.5 justify-center items-center flex">
          <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
        </div>
      </button>
      <button
        type="button"
        onClick={() => onSelect(value === false ? null : false)}
        disabled={disabled}
        aria-pressed={value === false}
        className={`${base} ${value === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
      >
        <div className="px-0.5 justify-center items-center flex">
          <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
        </div>
      </button>
    </div>
  );
}

export default function CypAdhdSsiDomainPanel({
  domain,
  stored,
  canEdit,
  notes,
  index,
  total,
  onImpairedSelect,
  onNotesChange,
  // Ticked example manifestations for this domain (HK testing, 2 Sept):
  // read from and written to data.functionalImpairment.{key}.selectedExamples,
  // the same shape the symptom panel uses.
  selectedExamples = [],
  onToggleExample,
  // Notes-box identity and the cross-page Move catalogue (see the symptom panel).
  notesDomainId,
  noteMoveTargets,
  onMoveNote,
}) {
  const hasExamples = (domain.exampleGroups || []).some(
    (group) => group?.options?.length > 0,
  );
  const hasPrompts = SHOW_SUGGESTED_PROMPTS && domain.suggestedPrompts?.length > 0;
  const hasLeftColumn = hasExamples || hasPrompts;

  return (
    <div className="bg-white p-6 rounded-lg mb-6">
      <div className="flex items-center justify-start gap-2 mb-1">
        <span className="text-gray-400 text-sm font-semibold">
          Area {index + 1} of {total}
        </span>
      </div>
      <div className="flex items-center justify-start gap-2 mb-3">
        <h2 className="flex items-center gap-2 text-gray-900 text-xl font-semibold leading-9 break-words text-left">
          {domain.title}
          {/* Clinical guidance, rendered only when content exists — the same
              affordance the symptom panel carries. */}
          {domain.tooltip && (
            <InfoTooltip
              content={domain.tooltip}
              label={`Clinical guidance for ${domain.title}`}
            />
          )}
        </h2>
      </div>

      <div className="flex mb-4">
        <div className="w-full text-left">
          <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex-col justify-start items-center gap-2 inline-flex">
            <div className="self-stretch flex-col justify-start items-center flex">
              <div className="self-stretch flex justify-end">
                <div className="h-10 flex items-center gap-3">
                  <div className="text-slate-700 text-lg font-medium leading-normal whitespace-nowrap">
                    At least moderate impairment:
                  </div>
                  <DecisionButtons
                    value={stored.isImpairedSelection ?? null}
                    onSelect={onImpairedSelect}
                    disabled={!canEdit}
                    groupLabel={`${domain.title} — at least moderate impairment?`}
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 my-4 self-stretch"></div>

              <div className="self-stretch justify-start items-start gap-2 inline-flex">
                {/* LEFT: grouped example manifestations and the suggested
                    prompts — things the clinician reads FROM, in the reading
                    position, exactly as the symptom panel arranges them. */}
                {hasLeftColumn && (
                  <>
                    <div className="w-1/2 flex flex-col gap-4">
                      {hasExamples && (
                        <div className="flex flex-col gap-3">
                          <label className="text-slate-700 text-sm font-semibold">
                            Examples
                          </label>
                          {domain.exampleGroups.map((group) => (
                            <div
                              key={group.title}
                              className="flex flex-col gap-1"
                              role="group"
                              aria-label={`${domain.title} — ${group.title} examples`}
                            >
                              {/* Sentence case, never all caps (design rules). */}
                              <h4 className="text-slate-600 text-sm font-bold">
                                {group.title}
                              </h4>
                              {group.options.map((example) => (
                                <label
                                  key={example}
                                  className="flex items-start gap-2 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    className="checkbox mt-1"
                                    checked={selectedExamples.includes(example)}
                                    onChange={() => onToggleExample?.(example)}
                                    disabled={!canEdit}
                                    aria-label={`Example: ${example}`}
                                  />
                                  <span className="text-slate-700 text-sm font-medium">
                                    {example}
                                  </span>
                                </label>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}

                      {hasPrompts && (
                        <div className={hasExamples ? 'border-t border-gray-200 pt-4' : ''}>
                          <h3 className="text-slate-700 text-sm font-bold mb-2">
                            Suggested prompts
                          </h3>
                          <ul className="list-disc pl-5 space-y-1">
                            {domain.suggestedPrompts.map((prompt) => (
                              <li key={prompt} className="text-sm text-gray-600">{prompt}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="border-l border-gray-200 mx-2 self-stretch"></div>
                  </>
                )}

                {/* RIGHT: what the clinician records. */}
                <div className={`${hasLeftColumn ? 'w-1/2' : 'w-full'} flex-col justify-start items-start gap-2 flex`}>
                  <label className="text-slate-700 text-sm font-semibold">
                    Clinical Notes
                  </label>
                  <div className="w-full">
                    <StructuredNotesEditor
                      notes={notes}
                      onChange={onNotesChange}
                      domainId={notesDomainId}
                      onMoveNoteIn={onMoveNote}
                      moveTargets={noteMoveTargets}
                      currentMoveId={notesDomainId}
                      onMoveNoteTo={onMoveNote}
                      placeholder="Add a note, then press Enter to save each point"
                      enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
                      tagOptions={CYP_NOTE_TAG_OPTIONS}
                      readOnly={!canEdit}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
